/**
 * Rebuilds one store slice from the manifest's declared fields only.
 *
 * Split out of `parseSettingsBundle` for the 200-line guideline; it is the
 * half that decides, field by field, what an untrusted file is allowed to
 * contribute. It REBUILDS rather than filters, so a key nobody thought about
 * cannot survive by being overlooked, and it throws on a denied key name so a
 * file carrying a credential stops the whole import.
 */
import { findDeniedKeyPattern, type ExportedField } from './settingsBundleManifest';
import { validateToolbarLayout } from './toolbarLayoutValidation';
import type { BundleWarning, StoreSlicePayload } from './settingsBundleTypes';
import { normalizeBetaFeatureOverrides } from '../../../core/beta';
import { normalizeOverrides } from '../../keybindings/store/normalizeOverrides';

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function typeMatches(value: unknown, type: NonNullable<ExportedField['type']>): boolean {
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  return typeof value === type;
}

export function sanitizeSlice(
  input: Record<string, unknown>,
  fields: ExportedField[],
  where: string,
  warnings: BundleWarning[],
): StoreSlicePayload {
  for (const key of Object.keys(input)) {
    const pattern = findDeniedKeyPattern(key);
    if (pattern) {
      throw new Error(
        `This backup contains "${key}", which can't be imported (it matched the ` +
          `never-travels rule "${pattern}"). The file was not applied.`,
      );
    }
  }

  const out: StoreSlicePayload = {};
  const declared = new Map(fields.map((f) => [f.name, f]));
  for (const [key, value] of Object.entries(input)) {
    const field = declared.get(key);
    if (!field) {
      warnings.push({
        code: 'unknown-field',
        message: `"${where}.${key}" isn't a setting this version knows — it was skipped.`,
      });
      continue;
    }
    if (field.type) {
      if (!typeMatches(value, field.type)) {
        warnings.push({
          code: 'wrong-type',
          message: `"${where}.${key}" expected ${field.type} — it was skipped.`,
        });
        continue;
      }
      out[key] = value;
      continue;
    }
    if (field.deep) {
      const kept = sanitizeDeep(key, value, `${where}.${key}`, warnings);
      if (kept !== undefined) out[key] = kept;
      continue;
    }
    if (!isRecord(value)) {
      warnings.push({
        code: 'wrong-type',
        message: `"${where}.${key}" expected a group of settings — it was skipped.`,
      });
      continue;
    }
    const nested = sanitizeSlice(value, field.fields ?? [], `${where}.${key}`, warnings);
    if (Object.keys(nested).length > 0) out[key] = nested;
  }
  return out;
}

/** `deep` fields have no per-key schema, so each gets a bespoke check. */
function sanitizeDeep(
  name: string,
  value: unknown,
  where: string,
  warnings: BundleWarning[],
): unknown {
  if (name === 'toolbarCustomization') {
    // `null` is the legitimate "this machine follows the shipped defaults"
    // value B14 persists, and it is what a non-customizing user's bundle
    // carries. Importing it is a real instruction (clear my customization),
    // not corruption.
    if (value === null) return null;
    const result = validateToolbarLayout(value);
    // A malformed layout is the one `deep` case that fails the whole import:
    // silently dropping it would leave the user with the toolbar they were
    // trying to replace and no explanation. See B11's re-review instruction.
    if (!result.ok) throw new Error(result.error);
    warnings.push(...result.warnings);
    return result.layout;
  }
  if (name === 'betaFeatures') {
    // Unlike `toolbarCustomization`, a bad payload here is never fatal: the
    // normalizer keeps the ids it recognises and drops the rest, and a dropped
    // id just means that feature keeps this build's default.
    if (!isRecord(value)) {
      warnings.push({ code: 'wrong-type', message: `"${where}" isn't a beta-feature map — it was skipped.` });
      return undefined;
    }
    const kept = normalizeBetaFeatureOverrides(value);
    if (Object.keys(kept).length < Object.keys(value).length) {
      warnings.push({
        code: 'unknown-field',
        message: `"${where}" carried beta features this version doesn't have — they were skipped.`,
      });
    }
    return kept;
  }
  if (name === 'snippetsByEngine') {
    if (!isRecord(value) || !Object.values(value).every(Array.isArray)) {
      warnings.push({ code: 'wrong-type', message: `"${where}" isn't a snippet list — it was skipped.` });
      return undefined;
    }
    return value;
  }
  if (name === 'overrides') {
    // Keyboard-shortcut overrides, through the keybindings store's own
    // normalizer so an import can never hold an override the store would not:
    // `[]` (an explicit unbinding) survives, unreadable chords do not. The
    // store re-validates on rehydrate (reserved chords, collisions — plan D6).
    if (!isRecord(value)) {
      warnings.push({ code: 'wrong-type', message: `"${where}" isn't a shortcut map — it was skipped.` });
      return undefined;
    }
    const normalized = normalizeOverrides(value);
    if (normalized.droppedCount > 0) {
      warnings.push({
        code: 'wrong-type',
        message: `"${where}" carried ${normalized.droppedCount} shortcut value(s) this version can't read — they were skipped.`,
      });
    }
    if (normalized.deniedIdCount > 0) {
      warnings.push({
        code: 'denied-key',
        message: `"${where}" carried ${normalized.deniedIdCount} shortcut(s) for commands whose names this version never imports — they were skipped.`,
      });
    }
    if (normalized.unknownOverrideIds.length > 0) {
      warnings.push({
        code: 'unknown-command',
        message: `"${where}" carried shortcuts for ${normalized.unknownOverrideIds.length} command(s) this version doesn't have — they are kept but do nothing here.`,
      });
    }
    return normalized.overrides;
  }
  warnings.push({ code: 'unknown-field', message: `"${where}" was skipped.` });
  return undefined;
}
